import { Banner } from '@components/display/banners/Banner';
import PanelCard from '@components/display/cards/PanelCard';
import { Button } from '@components/utility/buttons';
import { Tabs } from '@components/utility/tabs';
import { APP_PREFIX_PATH } from '@constants/common';
import VRMApiImage from '@developers/assets/images/vrm-api.png';
import { useAppDispatch } from '@hooks/generic';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const json = {
    idDataAttribute: 999,
    code: 'ATTR_CODE',
    timestamp: 123456789,
    valueFloat: 16,
    valueEnum: null,
    valueString: null,
    nameEnum: null,
    formatValueOnly: '%.2F',
    dataType: 'float',
    customLabel: 'Input Test',
};

export const VRMAPI = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'developers', defaultMessage: 'Developers' }),
                    to: APP_PREFIX_PATH + '/developers',
                    skipParams: true,
                },
                {
                    label: intl.formatMessage({
                        id: 'developers.vrm.breadcrumb',
                        defaultMessage: 'Victron VRM API',
                    }),
                    to: APP_PREFIX_PATH + '/developers/api-vrm/',
                },
            ])
        );
    }, [dispatch, intl]);

    return (
        <>
            <title>
                {intl.formatMessage({
                    id: 'developers.vrm.title',
                    defaultMessage: 'Victron Energy Professional - Developers - VRM API',
                })}
            </title>
            <Box component="main" p="16px">
                <Banner
                    data={{
                        header_title: (
                            <FormattedMessage
                                id="developers.vrm.header_title"
                                defaultMessage="Victron Remote Management API"
                            />
                        ),
                        header_subtitle: (
                            <FormattedMessage
                                id="developers.vrm.header_subtitle"
                                defaultMessage="Stay connected with the VRM API"
                            />
                        ),
                        header_text: (
                            <FormattedMessage
                                id="developers.vrm.header_text"
                                defaultMessage="Many of our customers integrate our products into their own systems, using data communication protocols. There are several options to establish data communication with the VRM API."
                            />
                        ),
                        button_text: (
                            <FormattedMessage id="developers.vrm.button_text" defaultMessage="Start with VRM API" />
                        ),
                        button_link: 'https://vrmapi.victronenergy.com/v2/docs',
                        background: 'light-blue',
                        image: VRMApiImage,
                        image_alt: 'Victron VRM API Banner',
                    }}
                    imgProps={{
                        className: '!object-contain !bg-transparent',
                    }}
                    buttonProps={{
                        target: '_blank',
                    }}
                />
                <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-1} pt="24px" pb="16px">
                    <FormattedMessage id="developers.victronwebsiteapi.api_endpoints" defaultMessage="API Endpoints" />
                </Typography>
                <Tabs value="website-connection" variant="scrollable">
                    <Tabs.Tab
                        value="website-connection"
                        label={
                            <FormattedMessage
                                id="developers.vrmapi.website_connection"
                                defaultMessage="Website Connection"
                            />
                        }
                    />
                </Tabs>
                <Grid2 container pt="30px" spacing="14px">
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <Stack spacing="14px">
                            <PanelCard
                                component="article"
                                header={
                                    <FormattedMessage
                                        id="developers.vrmapi.connect_website"
                                        defaultMessage="Connect your website with the VRM API"
                                    />
                                }
                                body={
                                    <FormattedMessage
                                        id="developers.vrmapi.connect_website.body"
                                        defaultMessage="Many of our customers integrate our products into their own systems, using data communication protocols. There are several options to establish data communication with the VRM API."
                                    />
                                }
                            />
                            <PanelCard
                                component="article"
                                header={
                                    <FormattedMessage
                                        id="developers.vrmapi.optimize_integration"
                                        defaultMessage="Optimize your VRM Integration"
                                    />
                                }
                                body={
                                    <FormattedMessage
                                        id="developers.vrmapi.optimize_integration.body"
                                        defaultMessage="Get the most out of the VRM portal. Our FAQ covers technical details on data intervals, site ID management, and advanced monitoring features."
                                    />
                                }
                                cta={
                                    <Box className="mt-auto pt-[26px]">
                                        <Button
                                            fullWidth
                                            fullRounded
                                            href="https://www.victronenergy.com/live/vrm_portal:faq"
                                            target="_blank"
                                        >
                                            <FormattedMessage
                                                id="developers.vrmapi.read_faq_button"
                                                defaultMessage="Read VRM FAQ"
                                            />
                                        </Button>
                                    </Box>
                                }
                            />
                        </Stack>
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <PanelCard.Code
                            className="h-full"
                            component="aside"
                            url="https://vrmapi.victronenergy.com/v2/users/3/installations"
                            json={json}
                        />
                    </Grid2>
                </Grid2>
            </Box>
        </>
    );
};
